import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Download, CheckCircle, ZoomIn, ZoomOut } from 'lucide-react';

export interface GalleryImage {
    url: string;
    name: string;
}

interface ImageGalleryOverlayProps {
    isOpen: boolean;
    onClose: () => void;
    images: GalleryImage[];
    initialIndex?: number;
}

const ImageGalleryOverlay: React.FC<ImageGalleryOverlayProps> = ({
    isOpen,
    onClose,
    images,
    initialIndex = 0,
}) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [downloading, setDownloading] = useState(false);
    const [downloadSuccess, setDownloadSuccess] = useState(false);
    const [zoomed, setZoomed] = useState(false);

    // Touch / swipe state
    const touchStartX = useRef<number | null>(null);
    const touchEndX = useRef<number | null>(null);
    const MIN_SWIPE = 50;

    useEffect(() => {
        if (isOpen) {
            setCurrentIndex(initialIndex);
            setZoomed(false);
        }
    }, [isOpen, initialIndex]);

    // Reset zoom when navigating
    useEffect(() => {
        setZoomed(false);
    }, [currentIndex]);

    const handleNext = useCallback((e?: React.MouseEvent) => {
        e?.stopPropagation();
        setCurrentIndex((prev) => (prev + 1) % images.length);
    }, [images.length]);

    const handlePrev = useCallback((e?: React.MouseEvent) => {
        e?.stopPropagation();
        setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    }, [images.length]);

    // Keyboard navigation
    useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowRight') handleNext();
            if (e.key === 'ArrowLeft') handlePrev();
            if (e.key === 'z' || e.key === 'Z') setZoomed(v => !v);
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, handleNext, handlePrev, onClose]);

    // Touch events
    const handleTouchStart = (e: React.TouchEvent) => {
        touchStartX.current = e.changedTouches[0].screenX;
    };
    const handleTouchEnd = (e: React.TouchEvent) => {
        touchEndX.current = e.changedTouches[0].screenX;
        if (touchStartX.current === null || touchEndX.current === null) return;
        const delta = touchStartX.current - touchEndX.current;
        if (Math.abs(delta) > MIN_SWIPE) {
            delta > 0 ? handleNext() : handlePrev();
        }
        touchStartX.current = null;
        touchEndX.current = null;
    };

    const downloadImage = async (url: string, name: string) => {
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            const objectUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = name;
            document.body.appendChild(link);
            link.click();
            window.URL.revokeObjectURL(objectUrl);
            document.body.removeChild(link);
        } catch {
            // Fallback: direct link
            const link = document.createElement('a');
            link.href = url;
            link.download = name;
            link.target = '_blank';
            link.click();
        }
    };

    const handleDownloadCurrent = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (downloading) return;
        setDownloading(true);
        const img = images[currentIndex];
        await downloadImage(img.url, img.name);
        setDownloading(false);
        setDownloadSuccess(true);
        setTimeout(() => setDownloadSuccess(false), 2000);
    };

    if (!isOpen || images.length === 0) return null;

    const currentImage = images[currentIndex];

    return (
        <div
            className="fixed inset-0 z-[200] bg-black/97 backdrop-blur-md flex items-center justify-center"
            style={{ animation: 'overlayFadeIn 0.2s ease' }}
            onClick={onClose}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
        >
            <style>{`
                @keyframes overlayFadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes imgPopIn {
                    from { opacity: 0; transform: scale(0.96); }
                    to { opacity: 1; transform: scale(1); }
                }
            `}</style>

            {/* ── Top Bar ── */}
            <div
                className="absolute top-0 left-0 right-0 z-50 flex items-center justify-between px-6 py-4 pointer-events-none"
                style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.8) 0%, transparent 100%)' }}
            >
                {/* Counter */}
                <div className="pointer-events-auto px-4 py-1.5 rounded-full bg-white/8 backdrop-blur border border-white/10 text-white/60 text-sm font-mono select-none">
                    {currentIndex + 1} / {images.length}
                </div>

                {/* Actions */}
                <div className="pointer-events-auto flex items-center gap-2">
                    {/* Zoom toggle */}
                    <button
                        onClick={(e) => { e.stopPropagation(); setZoomed(v => !v); }}
                        className="p-2.5 rounded-full bg-white/8 hover:bg-white/15 text-white/70 hover:text-white transition-all border border-white/10 backdrop-blur"
                        title={zoomed ? 'Zoom out (Z)' : 'Zoom in (Z)'}
                    >
                        {zoomed ? <ZoomOut size={18} /> : <ZoomIn size={18} />}
                    </button>

                    {/* Download current */}
                    <button
                        onClick={handleDownloadCurrent}
                        disabled={downloading}
                        className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all border backdrop-blur ${downloadSuccess
                                ? 'bg-emerald-500/20 border-emerald-500/30 text-emerald-400'
                                : 'bg-white/8 border-white/10 text-white/70 hover:bg-white/15 hover:text-white'
                            }`}
                        title="Download this image"
                    >
                        {downloadSuccess ? (
                            <><CheckCircle size={16} /> Saved!</>
                        ) : downloading ? (
                            <><Download size={16} className="animate-bounce" /> Saving…</>
                        ) : (
                            <><Download size={16} /> Download</>
                        )}
                    </button>

                    {/* Close */}
                    <button
                        onClick={(e) => { e.stopPropagation(); onClose(); }}
                        className="p-2.5 rounded-full bg-white/8 hover:bg-red-500/20 hover:text-red-400 text-white/70 transition-all border border-white/10 backdrop-blur"
                        title="Close (Esc)"
                    >
                        <X size={18} />
                    </button>
                </div>
            </div>

            {/* ── Main Image Area ── */}
            <div
                className="relative w-full h-full flex items-center justify-center px-16 md:px-24"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Prev */}
                {images.length > 1 && (
                    <button
                        onClick={handlePrev}
                        className="absolute left-3 md:left-6 z-50 p-3 rounded-full bg-black/50 hover:bg-white/10 text-white/50 hover:text-white transition-all border border-white/5 backdrop-blur group"
                    >
                        <ChevronLeft size={28} className="group-hover:-translate-x-0.5 transition-transform" />
                    </button>
                )}

                {/* Image */}
                <div
                    className={`relative rounded-xl overflow-hidden shadow-2xl shadow-black transition-all duration-300 ${zoomed ? 'cursor-zoom-out' : 'cursor-zoom-in'
                        }`}
                    onClick={() => setZoomed(v => !v)}
                    style={{ animation: 'imgPopIn 0.25s ease' }}
                >
                    <img
                        key={currentImage.url}
                        src={currentImage.url}
                        alt={currentImage.name}
                        className="object-contain select-none transition-all duration-300"
                        style={{
                            maxHeight: zoomed ? '95vh' : '80vh',
                            maxWidth: zoomed ? '95vw' : '78vw',
                        }}
                        draggable={false}
                    />
                </div>

                {/* Next */}
                {images.length > 1 && (
                    <button
                        onClick={handleNext}
                        className="absolute right-3 md:right-6 z-50 p-3 rounded-full bg-black/50 hover:bg-white/10 text-white/50 hover:text-white transition-all border border-white/5 backdrop-blur group"
                    >
                        <ChevronRight size={28} className="group-hover:translate-x-0.5 transition-transform" />
                    </button>
                )}
            </div>

            {/* ── Thumbnail Strip ── */}
            {images.length > 1 && (
                <div
                    className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2 overflow-x-auto max-w-[85vw] px-4 py-3 rounded-2xl border border-white/8 backdrop-blur-xl pointer-events-auto"
                    style={{ background: 'rgba(0,0,0,0.55)' }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {images.map((img, idx) => (
                        <button
                            key={idx}
                            onClick={() => setCurrentIndex(idx)}
                            className={`relative flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden border-2 transition-all duration-200 ${idx === currentIndex
                                    ? 'border-white scale-110 shadow-lg shadow-white/20 z-10'
                                    : 'border-transparent opacity-40 hover:opacity-90 hover:scale-105'
                                }`}
                        >
                            <img src={img.url} alt="" className="w-full h-full object-cover" draggable={false} />
                        </button>
                    ))}
                </div>
            )}

            {/* ── Image name pill (bottom center, above thumbnails) ── */}
            <div
                className="absolute pointer-events-none text-center"
                style={{ bottom: images.length > 1 ? '90px' : '20px', left: '50%', transform: 'translateX(-50%)' }}
            >
                <span className="px-3 py-1 rounded-full bg-black/50 text-white/40 text-xs font-mono border border-white/5 backdrop-blur truncate max-w-[50vw] inline-block">
                    {currentImage.name}
                </span>
            </div>
        </div>
    );
};

export default ImageGalleryOverlay;
